import {
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@shared/constants';
import { startNewConversationAction } from '@containers/App/containers/Messaging/actions';
import { UsersTableRow } from './types';
import {
  ActionButton,
  ActionMenuItem,
  QuickActionsRoot,
} from './styles';

type QuickActionsProps = {
  user: UsersTableRow;
  isTherapist: boolean;
  isVideoStarting: boolean;
  videoDisabled: boolean;
  onStartVideoChat: (user: UsersTableRow) => void;
  onOpenRecords: (user: UsersTableRow) => void;
};

type QuickAction = {
  key: string;
  label: string;
  icon: ReactElement;
  onClick: () => void;
  visible?: boolean;
  disabled?: boolean;
};

const QuickActions = ({
  user,
  isTherapist,
  isVideoStarting,
  videoDisabled,
  onStartVideoChat,
  onOpenRecords,
}: QuickActionsProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const fullName = `${user.firstName} ${user.lastName}`.trim();
  const menuOpen = Boolean(menuAnchor);

  const stopRowClick = (event: MouseEvent) => event.stopPropagation();
  const stopRowKeyDown = (event: KeyboardEvent) => event.stopPropagation();
  
  const confirmVideoChat = useCallback(() => {
    setMenuAnchor(null);
    onStartVideoChat(user);
  }, [onStartVideoChat, user]);

  const messageUser = useCallback(() => {
    setMenuAnchor(null);
    dispatch(startNewConversationAction({ ...user, fullName }));
    history.push(ROUTES.MESSAGING);
  }, [dispatch, fullName, history, user]);

  const openRecords = useCallback(() => {
    setMenuAnchor(null);
    onOpenRecords(user);
  }, [onOpenRecords, user]);

  const actions = useMemo<QuickAction[]>(() => [
    {
      key: 'videoChat',
      label: t('UsersTable.quickActions.videoChat'),
      icon: <VideocamOutlinedIcon fontSize="small" />,
      onClick: confirmVideoChat,
      visible: isTherapist,
      disabled: videoDisabled,
    },
    {
      key: 'message',
      label: t('UsersTable.quickActions.message'),
      icon: <MessageOutlinedIcon fontSize="small" />,
      onClick: messageUser,
      visible: isTherapist,
    },
    {
      key: 'userRecords',
      label: t('UsersTable.quickActions.userRecords'),
      icon: <DescriptionOutlinedIcon fontSize="small" />,
      onClick: openRecords,
    },
  ], [confirmVideoChat, isTherapist, messageUser, openRecords, t, videoDisabled]);

  return (
    <QuickActionsRoot
      onClick={stopRowClick}
      onMouseDown={stopRowClick}
      onKeyDown={stopRowKeyDown}
    >
      <ActionButton
        type="button"
        title={t('UsersTable.quickActions.title')}
        aria-label={t('UsersTable.quickActions.ariaLabel', { name: fullName })}
        aria-expanded={menuOpen}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        {isVideoStarting ? <CircularProgress size={16} /> : <MoreHorizIcon fontSize="small" />}
      </ActionButton>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actions.filter(({ visible = true }) => visible).map((action) => (
          <ActionMenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {action.icon}
            {action.label}
          </ActionMenuItem>
        ))}
      </Menu>
    </QuickActionsRoot>
  );
};

export default QuickActions;